import { useEffect, useState } from "react";
import type { DocumentTarget } from "./refPickerModel";
import { normalizeReport } from "../references/referencesPanelModel";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * The labels this document defines, fetched when the picker opens.
 *
 * Without `?numbers=true`: that costs a LaTeX run, and the picker opens on a
 * keystroke. The index itself is free, and a target the author has not yet
 * compiled shows the pending slot rather than a number we would have to invent.
 *
 * Refetched on every open rather than cached for the session, because the
 * document is being edited under it — a table captioned a minute ago must be
 * findable now. Derived, never stored, on the server for the same reason.
 */
export function useDocumentReferences(
  documentId: string | undefined,
  fetchWithAuth: Fetch | undefined,
  open: boolean,
): DocumentTarget[] {
  const [targets, setTargets] = useState<DocumentTarget[]>([]);

  useEffect(() => {
    if (!open || !documentId || !fetchWithAuth) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetchWithAuth(`/api/documents/${documentId}/references`);
        if (!res.ok) return;
        // Normalised: the API omits null fields, so an uncompiled target
        // arrives with no `number` key rather than number: null.
        const report = normalizeReport(await res.json());
        if (!cancelled) setTargets(report.targets);
      } catch {
        // A picker that cannot reach the index still offers the library.
        // Keeping the last list is better than flashing it empty.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, documentId, fetchWithAuth]);

  return targets;
}
